import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

// Simple Slot: renders the single child element with merged props (replaces @radix-ui/react-slot)
function Slot({ children, ...slotProps }: React.HTMLAttributes<HTMLElement>): JSX.Element {
  if (!React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)) {
    throw new Error('Slot requires a single valid React element child');
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return React.cloneElement(children, {
    ...slotProps,
    ...children.props,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    className: cn((slotProps as { className?: string }).className, children.props.className),
  });
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
}): JSX.Element {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

Button.defaultProps = { asChild: false };

export { Button, buttonVariants };
